import { Link } from 'react-router-dom';
import { ArrowRight, Compass } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { getNoteHeading } from '@/lib/noteUtils';

/**
 * Of the curated (pinned) records, the first one that has a real headline. Notes
 * without one are skipped rather than given an invented title.
 */
export function pickStartRecord(notes: ReadonlyArray<NostrEvent>): { note: NostrEvent; title: string } | null {
  for (const note of notes) {
    const { title } = getNoteHeading(note);
    if (title) return { note, title };
  }
  return null;
}

/**
 * "Start here": one curated record, offered to a first-time visitor as a
 * concrete place to begin, instead of leaving them with only categories and
 * counts. Deliberately small: a headline, a line about it, and one action.
 * It reuses a record that is already pinned, so it adds no second featured block.
 */
export function StartHere({ note, title }: { note: NostrEvent; title: string }) {
  const { description } = getNoteHeading(note, 140);

  return (
    <aside aria-label="Start here" className="mb-6">
      <Link
        to={`/${nip19.noteEncode(note.id)}`}
        state={{ fromArchive: true }}
        className="group flex items-center gap-3 rounded-2xl border border-foreground/30 bg-card p-3 shadow-[0_1px_2px_rgba(40,25,10,0.1)] transition-[transform,border-color] duration-150 ease-out hover:border-foreground/55 active:scale-[0.99] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [-webkit-tap-highlight-color:transparent] sm:p-4"
      >
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-foreground text-background"
        >
          <Compass className="h-5 w-5" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block font-engraved text-[10px] font-semibold uppercase tracking-[0.24em] text-flag-red">
            Start here
          </span>
          <span className="mt-0.5 line-clamp-2 block font-display text-lg leading-snug text-foreground">
            {title}
          </span>
          {description && (
            <span className="mt-0.5 line-clamp-2 block text-[13px] leading-snug text-foreground/70">
              {description}
            </span>
          )}
        </span>

        <span className="flex shrink-0 items-center gap-1.5 font-engraved text-[10px] font-bold uppercase tracking-[0.18em] text-foreground">
          <span className="hidden sm:inline">Read record</span>
          <ArrowRight
            aria-hidden="true"
            className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 motion-reduce:transition-none"
          />
          <span className="sr-only sm:hidden">Read record</span>
        </span>
      </Link>
    </aside>
  );
}
